import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Globe, Shield, AlertTriangle, Plus, Search, Filter, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { getDNSThreats, getDNSPolicies, createDNSPolicy, evaluateDNS } from '../services/api';
import { toast } from 'sonner';

const threatClassColors = {
  malicious: 'bg-red-500/10 text-red-500 border-red-500/20',
  suspicious: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  safe: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
};

const decisionColors = {
  block: 'bg-red-500/10 text-red-500',
  allow: 'bg-emerald-500/10 text-emerald-500',
};

export default function DNSThreats() {
  const [events, setEvents] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClass, setFilterClass] = useState('all');
  const [testDomain, setTestDomain] = useState('');
  const [newPolicy, setNewPolicy] = useState({ name: '', domain_pattern: '', action: 'block', category: 'custom' });
  const [showPolicyDialog, setShowPolicyDialog] = useState(false);
  const [showTestDialog, setShowTestDialog] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [eventsRes, policiesRes] = await Promise.all([
        getDNSThreats(),
        getDNSPolicies(),
      ]);
      setEvents(eventsRes.data);
      setPolicies(policiesRes.data);
    } catch (error) {
      console.error('Failed to fetch DNS data:', error);
      toast.error('Failed to load DNS data');
    } finally {
      setLoading(false);
    }
  };

  const handleTestDomain = async () => {
    if (!testDomain) return;
    try {
      const response = await evaluateDNS(testDomain);
      toast.success(`Domain evaluated: ${response.data.classification} (${response.data.decision})`);
      fetchData();
      setTestDomain('');
      setShowTestDialog(false);
    } catch (error) {
      toast.error('Failed to evaluate domain');
    }
  };

  const handleCreatePolicy = async () => {
    if (!newPolicy.name || !newPolicy.domain_pattern) return;
    try {
      await createDNSPolicy(newPolicy);
      toast.success('Policy created successfully');
      fetchData();
      setNewPolicy({ name: '', domain_pattern: '', action: 'block', category: 'custom' });
      setShowPolicyDialog(false);
    } catch (error) {
      toast.error('Failed to create policy');
    }
  };

  const filteredEvents = events.filter((event) => {
    const matchesSearch = event.domain.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterClass === 'all' || event.classification === filterClass;
    return matchesSearch && matchesFilter;
  });

  const stats = {
    total: events.length,
    blocked: events.filter((e) => e.decision === 'block').length,
    malicious: events.filter((e) => e.classification === 'malicious').length,
    suspicious: events.filter((e) => e.classification === 'suspicious').length,
  };

  return (
    <div className="space-y-6" data-testid="dns-threats-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl text-white">DNS Threats</h1>
          <p className="text-slate-400 mt-1">Monitor and manage DNS-level threat protection</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-slate-700 text-slate-300" data-testid="test-domain-btn">
                <Search className="w-4 h-4 mr-2" />
                Test Domain
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800">
              <DialogHeader>
                <DialogTitle className="text-white">Test Domain</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Domain</Label>
                  <Input
                    placeholder="example.com"
                    value={testDomain}
                    onChange={(e) => setTestDomain(e.target.value)}
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="test-domain-input"
                  />
                </div>
                <Button onClick={handleTestDomain} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="evaluate-domain-btn">
                  Evaluate
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={showPolicyDialog} onOpenChange={setShowPolicyDialog}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-policy-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Policy
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800">
              <DialogHeader>
                <DialogTitle className="text-white">Create DNS Policy</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Policy Name</Label>
                  <Input
                    placeholder="Block malicious domains"
                    value={newPolicy.name}
                    onChange={(e) => setNewPolicy({ ...newPolicy, name: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Domain Pattern</Label>
                  <Input
                    placeholder="*.malware.com"
                    value={newPolicy.domain_pattern}
                    onChange={(e) => setNewPolicy({ ...newPolicy, domain_pattern: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Action</Label>
                  <Select value={newPolicy.action} onValueChange={(v) => setNewPolicy({ ...newPolicy, action: v })}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="block">Block</SelectItem>
                      <SelectItem value="allow">Allow</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleCreatePolicy} className="w-full bg-blue-600 hover:bg-blue-700">
                  Create Policy
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Globe className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-white">{stats.total}</p>
                <p className="text-xs text-slate-500">Total Queries</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-white">{stats.blocked}</p>
                <p className="text-xs text-slate-500">Blocked</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-white">{stats.malicious}</p>
                <p className="text-xs text-slate-500">Malicious</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-white">{stats.suspicious}</p>
                <p className="text-xs text-slate-500">Suspicious</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters & Table */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="border-b border-slate-800">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <CardTitle className="text-lg font-heading text-white">DNS Events</CardTitle>
            <div className="flex gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  placeholder="Search domains..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-48 bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <Select value={filterClass} onValueChange={setFilterClass}>
                <SelectTrigger className="w-36 bg-slate-800 border-slate-700 text-white">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="malicious">Malicious</SelectItem>
                  <SelectItem value="suspicious">Suspicious</SelectItem>
                  <SelectItem value="safe">Safe</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" onClick={fetchData} className="border-slate-700">
                <RefreshCw className="w-4 h-4 text-slate-400" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="text-slate-400">Domain</TableHead>
                <TableHead className="text-slate-400">Risk Score</TableHead>
                <TableHead className="text-slate-400">Classification</TableHead>
                <TableHead className="text-slate-400">Decision</TableHead>
                <TableHead className="text-slate-400">Timestamp</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEvents.map((event, index) => (
                <motion.tr
                  key={event.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.02 }}
                  className="border-slate-800 hover:bg-slate-800/50"
                >
                  <TableCell className="font-mono text-sm text-white">{event.domain}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-2 bg-slate-800 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            event.risk_score >= 75 ? 'bg-red-500' :
                            event.risk_score >= 50 ? 'bg-orange-500' :
                            event.risk_score >= 25 ? 'bg-amber-500' : 'bg-emerald-500'
                          }`}
                          style={{ width: `${event.risk_score}%` }}
                        />
                      </div>
                      <span className="text-sm text-slate-400">{event.risk_score}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={`${threatClassColors[event.classification]} border`}>
                      {event.classification}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={decisionColors[event.decision]}>
                      {event.decision}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-slate-400">
                    {new Date(event.timestamp).toLocaleString()}
                  </TableCell>
                </motion.tr>
              ))}
              {filteredEvents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    No DNS events found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Policies */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-white">DNS Policies ({policies.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {policies.map((policy) => (
              <div key={policy.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
                <div>
                  <p className="text-sm font-medium text-white">{policy.name}</p>
                  <p className="text-xs text-slate-500 font-mono">{policy.domain_pattern}</p>
                </div>
                <Badge className={policy.action === 'block' ? 'bg-red-500/10 text-red-500' : 'bg-emerald-500/10 text-emerald-500'}>
                  {policy.action}
                </Badge>
              </div>
            ))}
            {policies.length === 0 && (
              <p className="text-center py-4 text-slate-500">No custom policies configured</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
